export default function SantaRosaTravelTimingSection() {
  return (
    <section className="bg-[#F7F8FA] py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-14">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Regional Travel Expertise</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">
            Travel &amp; Passport Timing for Santa Rosa Residents
          </h2>
          <p className="text-gray-500 max-w-2xl mx-auto text-base leading-relaxed">
            Understanding how Santa Rosa and Sonoma County travelers actually move helps us anticipate the passport urgencies that arise here. From wine country business trips to Oakmont retirement tours, North Bay travel has its own rhythm.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-goblet-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Wine Industry International Travel</h3>
                <p className="text-gray-400 text-xs">Bordeaux, Burgundy, Tuscany, Rioja, New Zealand</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Sonoma County's wine industry generates consistent international travel demand. Winemakers attend Vinexpo in Bordeaux, harvest consultations in Italy, and buyer meetings in Hong Kong. Vineyard owners travel to wine competitions in Europe. Hospitality executives from the dozens of resort properties in the region attend global tourism conferences. These trips are often confirmed weeks in advance with non-negotiable departure windows.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Wine industry travel windows align with harvest and trade show seasons — they cannot be rescheduled. Renew your passport at the start of each new decade of use, not when you have a trip booked.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-flight-takeoff-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">STS / SF Airport Access</h3>
                <p className="text-gray-400 text-xs">Sonoma County Airport &amp; San Francisco International</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Santa Rosa travelers use two main airports: the Sonoma County Airport (STS) for regional and domestic connections, and San Francisco International (SFO) about 60 miles south for the full range of international routes. SFO's direct service to Tokyo, Seoul, Frankfurt, Paris, and Latin America makes it the primary gateway for Santa Rosa's international travelers — meaning last-minute SFO bookings create regular expedited passport demand in Sonoma County.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Check your passport expiration before booking any SFO international flight. Most destinations require six months validity beyond your return date.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-home-heart-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">Oakmont &amp; Senior Retirement Travel</h3>
                <p className="text-gray-400 text-xs">European river cruises, Mediterranean tours, South American adventures</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Oakmont Village — one of California's largest active adult retirement communities with over 6,000 residents — generates significant passport renewal demand. Many residents planned international bucket-list trips during the pandemic years when their passports were expiring. As travel reopened, the backlog created a wave of urgent renewal requests. We regularly process expedited renewals for Oakmont residents planning European river cruises, Mediterranean tours, and Peruvian heritage trips on tight timelines.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> Senior travelers should renew before booking a cruise or tour package. Many tour operators require passport copies at booking — not just at departure.
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-7 border border-gray-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#1A365D]/5 flex-shrink-0">
                <i className="ri-graduation-cap-line text-[#1A365D] text-lg"></i>
              </div>
              <div>
                <h3 className="font-bold text-[#1A365D] text-sm">SRJC Students &amp; Young Adult Travel</h3>
                <p className="text-gray-400 text-xs">Study abroad, volunteer programs, international travel seasons</p>
              </div>
            </div>
            <p className="text-gray-500 text-sm leading-relaxed mb-4">
              Santa Rosa Junior College sends hundreds of students abroad each year on study programs, language immersion trips, and service-learning experiences. Many students are first-time passport applicants who do not realize they need an in-person acceptance clerk visit before expediting. Additionally, SRJC's significant Latino student population frequently travels to Mexico and Central America for family visits, creating a steady base of urgent passport requests throughout the year.
            </p>
            <div className="flex items-start gap-2 bg-[#F6C344]/8 rounded-md p-3">
              <i className="ri-lightbulb-flash-line text-[#F6C344] text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-gray-600 text-xs leading-relaxed">
                <strong className="text-[#1A365D]">Passport tip:</strong> First-time applicants must visit an acceptance clerk in person — we provide the exact Santa Rosa locations and a personalized checklist so your SRJC study-abroad program is not delayed.
              </p>
            </div>
          </div>
        </div>

        <div className="bg-[#1A365D] rounded-lg p-7 flex flex-col md:flex-row items-center gap-6">
          <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#F6C344]/15 flex-shrink-0">
            <i className="ri-heart-pulse-line text-[#F6C344] text-2xl"></i>
          </div>
          <div className="flex-1 text-center md:text-left">
            <h3 className="font-bold text-white text-base mb-2">Healthcare Professionals &amp; Kaiser/Sutter Travel</h3>
            <p className="text-white/60 text-sm leading-relaxed">
              Santa Rosa is a major healthcare hub with Kaiser Permanente and Sutter Health facilities. Healthcare professionals regularly attend international medical conferences in Europe, Asia, and Latin America — often with 2–4 week notice windows. We accommodate urgent medical professional timelines with same-day document review and expedited submission.
            </p>
          </div>
          <a
            href="https://fastpassportcenter.com/checkout/"
            className="bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-6 py-3 rounded-md transition-all whitespace-nowrap flex-shrink-0"
          >
            Get Started <i className="ri-arrow-right-line ml-1"></i>
          </a>
        </div>
      </div>
    </section>
  );
}